<template>
  <div class="cpd-activity-page">
    <v-card class="cpd-activity-card">
      <div class="cpd-activity-header">
        <div class="cpd-activity-header__copy">
          <div class="cpd-activity-title">CPD Activities</div>
          <div class="cpd-activity-subtitle">Manage CPD activity schedules and providers.</div>
        </div>
        <v-btn
            v-if="canCreateProgram"
            class="cpd-activity-add-button"
            depressed
            @click="addProgram()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add CPD Activity</span>
        </v-btn>
      </div>

      <v-card-title class="cpd-activity-filter">
        <v-row align="center" class="cpd-activity-filter__row" no-gutters>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-autocomplete
                v-model="regionValue"
                :clearable="clearable"
                :items="REGIONS"
                :loading="regionLoading"
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-map-marker-radius"
                dense
                outlined
                hide-details
                label="Filter by region"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-autocomplete
                v-model="cpdProviderValue"
                :clearable="clearable"
                :items="CPD_PROVIDERS"
                :loading="cpdProviderLoading"
                item-text="trainingCenterName"
                item-value="id"
                prepend-inner-icon="mdi-filter-variant"
                dense
                outlined
                hide-details
                label="Filter by CPD provider"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-select
                v-model="categoryTypeValue"
                :clearable="clearable"
                :items="CATEGORY_TYPES"
                item-text="text"
                item-value="value"
                prepend-inner-icon="mdi-shape-outline"
                dense
                outlined
                hide-details
                label="Filter by category type"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-menu
                ref="menuStartDate"
                v-model="startMenu"
                :close-on-content-click="false"
                max-width="290px"
                min-width="290px"
                offset-y
                transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="cpdStartDateValue"
                    :clearable="true"
                    dense
                    outlined
                    hide-details
                    label="CPD activity start date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                  ref="date"
                  v-model="cpdStartDateValue"
                  no-title
                  scrollable
                  @input="startMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-menu
                ref="menuEndDate"
                v-model="startMenu2"
                :close-on-content-click="false"
                max-width="290px"
                min-width="290px"
                offset-y
                transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="cpdEndDateValue"
                    :clearable="true"
                    dense
                    outlined
                    hide-details
                    label="CPD activity end date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                  ref="date"
                  v-model="cpdEndDateValue"
                  no-title
                  scrollable
                  @input="startMenu2 = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="2" class="cpd-activity-filter__col">
            <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                prepend-inner-icon="mdi-text-search"
                dense
                outlined
                hide-details
                label="Search CPD activity"
                single-line
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
          :headers="headers"
          :items="programs.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="programs.count"
          class="cpd-activity-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.programType.name`]="{ item }">
          <label for="item">{{
              item.programType ? item.programType.name : "-"
            }}</label>
        </template>
        <template v-slot:[`item.category.name`]="{ item }">
          <label for="item">{{
              item.category ? item.category.name : "-"
            }}</label>
        </template>
        <template v-slot:[`item.cpdProvider.trainingCenterName`]="{ item }">
          <label for="item">{{
              item.cpdProvider ? item.cpdProvider.trainingCenterName : "-"
            }}</label>
        </template>
        <template v-slot:[`item.courseTitleCat2`]="{ item }">
          <label for="item">{{
              item.courseTitleCat2 ? item.courseTitleCat2 : (item.program_courses.length !== 0 ? item.program_courses[0].course.courseName : "-")
            }}</label>
        </template>
        <template v-slot:[`item.ceu`]="{ item }">
          <label for="item">{{
              item.ceu ? item.ceu : (item.program_courses.length !== 0 ? item.program_courses[0].course.continualEducationUnit : "-")
            }}</label>
        </template>
        <template v-slot:[`item.facilitatedBy`]="{ item }">
          <label for="item">{{
              item.facilitatedBy ? item.facilitatedBy : "-"
            }}</label>
        </template>
        <template v-slot:[`item.participantsNumber`]="{ item }">
          <label for="item">{{
              item.participantsNumber ? item.participantsNumber : "-"
            }}</label>
        </template>
        <template v-slot:[`item.trainersNumber`]="{ item }">
          <label for="item">{{
              item.trainersNumber ? item.trainersNumber : "-"
            }}</label>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="cpd-activity-action-group">
            <v-tooltip bottom>
              <template v-if="canViewProgram" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-activity-action-icon"
                    @click="viewProgram(item)"
                    v-on="on"
                >mdi-eye
                </v-icon
                >
              </template>
              <span>View CPD Activity</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canEditProgram" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-activity-action-icon"
                    @click="editProgram(item)"
                    v-on="on"
                >mdi-circle-edit-outline
                </v-icon
                >
              </template>
              <span>Edit CPD Activity</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canDeleteProgram && item.canDelete" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-activity-action-icon cpd-activity-action-icon--danger"
                    @click="requestDelete(item)"
                    v-on="on"
                >mdi-delete-outline
                </v-icon
                >
              </template>
              <span>Delete CPD Activity</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="60%">
        <v-card class="cpd-activity-dialog-card">
          <v-card-title
              class="cpd-activity-dialog__title"
              primary-title
          >{{ title }}
            <v-spacer></v-spacer>

            <v-btn class="cpd-activity-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddProgram
              v-if="dialog"
              :changeCategoryNo="changeCategoryNo"
              :changeProgramTypeNo="changeProgramTypeNo"
              :program="program"
              @close="closeDialog()"
              @registerOrUpdateProgram="registerOrUpdateProgram"
          ></HrdAddProgram>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="cpd-activity-dialog-card">
          <v-card-title
              class="cpd-activity-dialog__title"
              primary-title
          >
            {{ title }}
            <v-spacer></v-spacer>

            <v-btn class="cpd-activity-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>
          <HrdProgramDetail
              v-if="detailDialog"
              :program="program"
          ></HrdProgramDetail>
        </v-card>
      </v-dialog>
    </template>
    <confirmationModal
        v-if="deleteDialog"
        title="Delete CPD Activity"
        @confirmed="confirmDelete"
        @close="closeDelete"
    ></confirmationModal>
  </div>
</template>


<script>
import HrdAddProgram from "./hrd-add-program";
import HrdProgramDetail from "./hrd-program-detail";
import {ProgramService} from "./programs.service";
import {getUser} from "@/utils/helper";
import {TrainingCenterService} from "../TrainingCenter/training_center.service";
import {MfrService} from "../Lookups/mfr/mfr.service";
import confirmationModal from "../../common/confirmation-modal.vue";

export default {
  components: {
    HrdAddProgram,
    HrdProgramDetail,
    confirmationModal,
  },

  data() {
    return {
      dialog: false,
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: "",
      programs: [],
      headers: [
        {text: "CPD Activity Type", value: "programType.name"},
        {text: "CPD Activity", value: "category.name"},
        {text: "CPD Activity Provider", value: "cpdProvider.trainingCenterName"},
        {text: "Course Name", value: "courseTitleCat2"},
        {text: "CEU", value: "ceu"},
        {text: "Facilitated By", value: "facilitatedBy"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      loading: false,
      program: null,
      changeProgramTypeNo: null,
      changeCategoryNo: null,
      cpdProviderLoading: false,
      CPD_PROVIDERS: [],
      regionLoading: false,
      REGIONS: [],
      searchable: true,
      clearable: true,
      cpdProviderValue: null,
      regionValue: null,
      categoryTypeValue: null,
      startMenu: false,
      startMenu2: false,
      cpdStartDateValue: null,
      cpdEndDateValue: null,
      searchDebounce: null,
      CATEGORY_TYPES: [
        {text: "Category I", value: "CAT1"},
        {text: "Category II", value: "CAT2"},
      ],
      deleteDialog: false,
      programToDelete: null,
      deleting: false,
    };
  },
  async beforeMount() {
    this.fetchProgramList();
    this.cpdProviderLoading = true;
    this.regionLoading = true;
    const trainingCenterResp = await TrainingCenterService.getAllTrainingCenters();
    this.CPD_PROVIDERS = trainingCenterResp.data.rows;
    const regionResp = await MfrService.getRegionsDrp();
    const regions = regionResp?.data || [];
    this.REGIONS = Array.isArray(regions)
        ? regions.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
        : [];
    this.cpdProviderLoading = false;
    this.regionLoading = false;
  },
  watch: {
    options: {
      handler() {
        this.fetchProgramList();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchProgramList();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchProgramList();
      }, 400);
    },
    cpdProviderValue: function (val) {
      this.cpdProviderValue = val == null ? null : val;
      this.fetchProgramList();
    },
    regionValue: function (val) {
      this.regionValue = val == null ? null : val;
      this.fetchProgramList();
    },
    categoryTypeValue: function (val) {
      this.categoryTypeValue = val == null ? null : val;
      this.fetchProgramList();
    },
    cpdStartDateValue: function (val) {
      this.cpdStartDateValue = val == '' ? null : val;
      this.fetchProgramList();
    },
    cpdEndDateValue: function (val) {
      this.cpdEndDateValue = val == '' ? null : val;
      this.fetchProgramList();
    }
  },
  computed: {
    canCreateProgram() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName === "CPD Activity" && permission.canCreate
      );
    },
    canEditProgram() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName === "CPD Activity" && permission.canUpdate
      );
    },
    canViewProgram() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName === "CPD Activity" && permission.canRead
      );
    },
    canDeleteProgram() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName === "CPD Activity" && permission.canDelete
      );
    },
  },
  methods: {
    normalizeDate(value) {
      if (!value) return null;
      const dateStr = String(value).trim();
      if (!dateStr) return null;
      if (dateStr.includes("T")) {
        return dateStr.split("T")[0];
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      const parsed = new Date(dateStr);
      if (Number.isNaN(parsed.getTime())) {
        return dateStr;
      }
      return parsed.toISOString().slice(0, 10);
    },
    normalizeId(value) {
      if (value === null || value === undefined || value === "") return null;
      const id = Number(value);
      return Number.isNaN(id) ? null : id;
    },
    resolveApiResponse(res) {
      const responseBody = res?.data ?? res ?? {};
      const apiStatus = responseBody?.status ?? responseBody?.statusCode ?? null;
      const resultCode = responseBody?.resultCode ?? null;
      const httpStatus = res?.status ?? res?.statusCode ?? null;
      const isApiSuccess =
          resultCode === 1 || (apiStatus != null && [200, 201].includes(apiStatus));
      const isHttpSuccess = httpStatus != null && [200, 201].includes(httpStatus);
      const isSuccess =
          apiStatus != null || resultCode != null ? isApiSuccess : isHttpSuccess;
      return {responseBody, apiStatus, isSuccess};
    },
    extractCourseId(program) {
      return (
          program?.course?.id ??
          program?.courseId ??
          program?.program_courses?.[0]?.courseId ??
          program?.program_courses?.[0]?.course?.id ??
          null
      );
    },
    extractCpdProviderId(program) {
      return (
          program?.cpdProviderId ??
          program?.cpd_provider_id ??
          program?.cpdProvider?.id ??
          program?.trainingCenterId ??
          null
      );
    },
    extractCourseLevelId(program) {
      return (
          program?.courseLevelId ??
          program?.courseLevel?.id ??
          program?.program_courses?.[0]?.courseLevelId ??
          program?.program_courses?.[0]?.course_level_id ??
          program?.program_courses?.[0]?.courseLevel?.id ??
          program?.program_courses?.[0]?.course_level?.id ??
          null
      );
    },
    async hasDuplicateProgram(program) {
      const startDate = this.normalizeDate(program?.startDate);
      const endDate = this.normalizeDate(program?.endDate);
      const courseId = this.normalizeId(this.extractCourseId(program));
      const cpdProviderId = this.normalizeId(this.extractCpdProviderId(program));
      const courseLevelId = this.normalizeId(this.extractCourseLevelId(program));
      const programId = this.normalizeId(program?.id);

      if (!startDate || !endDate || !courseId || !cpdProviderId || !courseLevelId) {
        return false;
      }

      const res = await ProgramService.getProgramByDate(startDate, endDate);
      const payload = res?.data ?? res;
      const programs = Array.isArray(payload) ? payload : payload?.rows ?? [];
      if (!Array.isArray(programs) || !programs.length) {
        return false;
      }

      return programs.some((item) => {
        if (programId && this.normalizeId(item?.id) === programId) return false;
        const itemCourseId = this.normalizeId(this.extractCourseId(item));
        const itemCpdProviderId = this.normalizeId(this.extractCpdProviderId(item));
        const itemCourseLevelId = this.normalizeId(this.extractCourseLevelId(item));
        const itemStartDate = this.normalizeDate(item?.startDate);
        const itemEndDate = this.normalizeDate(item?.endDate);
        return (
            itemCourseId === courseId &&
            itemCpdProviderId === cpdProviderId &&
            itemCourseLevelId === courseLevelId &&
            itemStartDate === startDate &&
            itemEndDate === endDate
        );
      });
    },
    async fetchProgramList() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      let searchParams = '';
      const trimmedSearch = this.search ? this.search.trim() : "";
      trimmedSearch && (searchParams += `&name=${encodeURIComponent(trimmedSearch)}`);
      this.cpdProviderValue !== null && (searchParams += '&trainingCenterId=' + this.cpdProviderValue);
      this.regionValue !== null && (searchParams += '&regionId=' + this.regionValue);
      this.categoryTypeValue !== null && (searchParams += '&categoryType=' + this.categoryTypeValue);
      this.cpdStartDateValue !== null && (searchParams += '&startDate=' + this.cpdStartDateValue);
      this.cpdEndDateValue !== null && (searchParams += '&endDate=' + this.cpdEndDateValue);

      const res = await ProgramService.getProgramsWP(
          page,
          itemsPerPage,
          searchParams
      );
      if (res.status === 200) {
        this.loading = false;
        const payload = res.data || {};
        if (Array.isArray(payload.rows)) {
          const sortedRows = this.sortProgramsByLatestDate(payload.rows);
          this.programs = {
            ...payload,
            rows: sortedRows,
          };
        } else {
          this.programs = payload;
        }
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching CPD Activity, Please try Again!"
        );
      }
    },
    sortProgramsByLatestDate(rows) {
      const indexed = rows.map((item, index) => ({item, index}));
      indexed.sort((a, b) => {
        const aStartTime = this.toDateTime(a.item?.startDate ?? a.item?.start_date);
        const bStartTime = this.toDateTime(b.item?.startDate ?? b.item?.start_date);
        if (aStartTime == null && bStartTime != null) return 1;
        if (aStartTime != null && bStartTime == null) return -1;
        if (aStartTime != null && bStartTime != null && aStartTime !== bStartTime) {
          return bStartTime - aStartTime;
        }

        const aEndTime = this.toDateTime(a.item?.endDate ?? a.item?.end_date);
        const bEndTime = this.toDateTime(b.item?.endDate ?? b.item?.end_date);
        if (aEndTime == null && bEndTime != null) return 1;
        if (aEndTime != null && bEndTime == null) return -1;
        if (aEndTime != null && bEndTime != null && aEndTime !== bEndTime) {
          return bEndTime - aEndTime;
        }

        const aCreatedTime = this.toDateTime(a.item?.createdAt ?? a.item?.created_at ?? a.item?.createdDate);
        const bCreatedTime = this.toDateTime(b.item?.createdAt ?? b.item?.created_at ?? b.item?.createdDate);
        if (aCreatedTime == null && bCreatedTime == null) return a.index - b.index;
        if (aCreatedTime == null) return 1;
        if (bCreatedTime == null) return -1;
        if (aCreatedTime === bCreatedTime) return a.index - b.index;
        return bCreatedTime - aCreatedTime;
      });
      return indexed.map((entry) => entry.item);
    },
    toDateTime(value) {
      if (!value) return null;
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? null : time;
    },
    viewProgram(item) {
      this.program = item;
      this.detailDialog = true;
      this.title = "CPD Activity Detail";
    },
    addProgram() {
      this.program = null;
      this.dialog = true;
      this.title = "Add CPD Activity";
    },
    editProgram(item) {
      this.program = item;
      this.dialog = true;
      this.title = "Edit CPD Activity";
      this.changeProgramTypeNo = 0;
      this.changeCategoryNo = 0;
    },
    requestDelete(item) {
      this.programToDelete = item;
      this.deleteDialog = true;
    },
    closeDelete() {
      this.deleteDialog = false;
      this.programToDelete = null;
    },
    async confirmDelete() {
      if (!this.programToDelete || this.deleting) {
        return;
      }

      this.deleting = true;
      try {
        const res = await ProgramService.delete(this.programToDelete.id, this.programToDelete);
        if (res.status === 200) {
          if (res.data?.resultCode === 1) {
            this.$toast.success(res.data.message);
            this.fetchProgramList();
          } else {
            this.$toast.error(res.data?.message || "Unable to delete CPD Activity, please try again");
          }
        } else {
          this.$toast.error("Unable to delete CPD Activity, please try again");
        }
      } catch (error) {
        this.$toast.error("Unable to delete CPD Activity, please try again");
      } finally {
        this.deleting = false;
        this.closeDelete();
      }
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.fetchProgramList();
    },
    async registerOrUpdateProgram(program) {
      let res = null;
      let hasDuplicate = false;

      try {
        hasDuplicate = await this.hasDuplicateProgram(program);
      } catch (error) {
        this.$toast.error("Unable to validate CPD Activity, please try again.");
        return;
      }

      if (hasDuplicate) {
        this.$toast.error(
            "CPD Activity already available for the selected course, course level, and date range."
        );
        return;
      }

      if (program.editMode) {
        try {
          res = await ProgramService.update(program.id, program);
          const {responseBody, apiStatus, isSuccess} = this.resolveApiResponse(res);

          if (isSuccess) {
            this.$toast.success(responseBody?.message || "CPD Activity updated successfully.");
            this.closeDialog();
          } else if (apiStatus === 500) {
            this.$toast.error(responseBody?.message || "Unable to update CPD Activity, please try again");
          } else {
            this.$toast.error("Unable to update CPD Activity, please try again");
          }
        } catch (error) {
          this.$toast.error("Unable to update CPD Activity, please try again");
        }
      } else {
        try {
          res = await ProgramService.create(program);
          const {responseBody, apiStatus, isSuccess} = this.resolveApiResponse(res);

          if (isSuccess) {
            this.$toast.success(responseBody?.message || "CPD Activity created successfully.");
            this.closeDialog();
          } else if (apiStatus === 500) {
            this.$toast.error(responseBody?.message || "Unable to create CPD Activity, please try again");
          } else {
            this.$toast.error("Unable to create CPD Activity, please try again");
          }
        } catch (error) {
          this.$toast.error("Unable to create CPD Activity, please try again");
        }
      }
    },
  },
  beforeDestroy() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
};
</script>
<style scoped>
.cpd-activity-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.cpd-activity-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.cpd-activity-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.cpd-activity-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cpd-activity-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.cpd-activity-subtitle {
  font-size: 14px;
  color: #475569;
}

.cpd-activity-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.cpd-activity-filter {
  padding: 0 20px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.cpd-activity-filter__row {
  margin: 0 -8px;
  flex-wrap: wrap;
}

.cpd-activity-filter__col {
  padding: 8px;
}

.cpd-activity-table {
  border-radius: 0 0 12px 12px;
}

.cpd-activity-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.cpd-activity-action-group .v-tooltip {
  display: inline-flex;
}

.cpd-activity-action-icon {
  color: #1976d2 !important;
}

.cpd-activity-action-icon--danger {
  color: #ef4444 !important;
}

.cpd-activity-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.cpd-activity-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.cpd-activity-dialog__close {
  color: #ffffff !important;
}

.cpd-activity-dialog__close .v-icon {
  color: #ffffff !important;
}

@media (max-width: 700px) {
  .cpd-activity-page {
    padding: 12px;
  }

  .cpd-activity-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .cpd-activity-add-button {
    width: 100%;
    justify-content: center;
  }

  .cpd-activity-filter {
    padding: 0 12px 12px;
  }
}
</style>
